import { statusCategory, tagsCategory, useInboxBarFilters } from '../shared/inboxFilters';
import { useLoadErrorToast } from '../shared/useLoadErrorToast';
import { useSearchInput } from '../shared/useSearchInput';
import { useLeavingMessageRows } from './useLeavingMessageRows';
import { useMessageApiFilters } from './useMessageApiFilters';
import { useMessageInboxQuery } from './useMessageInboxQuery';
import { useMessageListUrlState } from './useMessageListUrlState';

const PAGE_SIZE = 20;

function useMessageFilters(table: ReturnType<typeof useMessageListUrlState>) {
    const { practiceIds, assignees, tags } = table.filters;
    const { setFilter } = table;
    const { apiFilters, assigneeOptions, assigneesResolved } = useMessageApiFilters(
        {
            search: table.search,
            practiceIds,
            assignees,
            tags,
            status: table.status,
            sort: table.sort,
            order: table.order
        },
        (values) => setFilter('assignees', values)
    );
    const filters = useInboxBarFilters({ assigneeOptions, assignees, practiceIds, setFilter });
    const more = [
        statusCategory(table.status, table.setStatus),
        tagsCategory(tags, (value) => setFilter('tags', value))
    ];
    const moreActiveCount = [table.status === 'closed', tags.length > 0].filter(Boolean).length;
    return { filters, more, moreActiveCount, apiFilters, assigneesResolved };
}

export function useMessagesInbox() {
    const table = useMessageListUrlState(PAGE_SIZE);
    const [searchInput, setSearchInput] = useSearchInput(table.search, table.setSearch);
    const { filters, more, moreActiveCount, apiFilters, assigneesResolved } =
        useMessageFilters(table);
    const { query, loadMore } = useMessageInboxQuery({
        filters: apiFilters,
        mode: table.mode,
        page: table.page,
        limit: table.limit,
        enabled: assigneesResolved
    });
    useLoadErrorToast(query.isError, query.error, 'Failed to load DM messages');

    const { practiceIds, assignees, tags } = table.filters;
    const hasFilters =
        practiceIds.length > 0 ||
        assignees.length > 0 ||
        tags.length > 0 ||
        table.search.length > 0;
    const clearFilters = () => {
        setSearchInput('');
        table.clearFilters();
    };
    const { rows, markPatientLeaving, findListedPatient } = useLeavingMessageRows(query.rows);

    return {
        table,
        query,
        rows,
        loadMore,
        search: { value: searchInput, onChange: setSearchInput },
        filters,
        more,
        moreActiveCount,
        hasFilters,
        clearFilters,
        markPatientLeaving,
        findListedPatient
    };
}

export type MessagesInbox = ReturnType<typeof useMessagesInbox>;
